import React, {
  ChangeEventHandler,
  CSSProperties,
  HTMLProps,
  ReactElement,
} from 'react';
import ReactDatePicker from 'react-datepicker';
import { format, setHours, setMinutes } from 'date-fns';
import styled from 'styled-components';

import { useInputFocusContext } from 'components/forms/ResponseOptions/providers/InputFocusProvider';
import { Input as InputWeb } from 'components/primitives/web-only';
import { useGetDynamicInputStyles } from 'components/ResponsiveLayout';
import { roles } from 'cross-platform/utils/roleProps';
import { getDateWithoutMilliseconds } from 'lib/getDateWithoutMilliseconds';
import {
  getStylesForInputStates,
  InputStates,
} from 'lib/styles/inputStateStyles';

import { createTimezoneOffsetDate } from './utils/createTimezoneOffsetDate';
import { getCurrentMinTime, getMinTime } from './utils/getMinTime';
import { isBeforeMinTime } from './utils/isBeforeMinTime';
import { transformDateOnChange } from './utils/transformDateOnChange';
import {
  DatePickerDisplayFormat,
  DatePickerPlaceholder,
  RawDatePickersProps,
} from './types';

import 'react-datepicker/dist/react-datepicker.css';

/**
 * Util function for onChange handler
 *
 * @param changedDate from onChange
 * @param date previous selected
 * @param mode type of picker
 * @returns
 */
const tryTransformDateOnChange = (
  changedDate: Date | null,
  date: Date | null,
  mode: 'date' | 'datetime' | 'time'
): Date | null => {
  let newDate = changedDate;
  const oldDate = date || undefined;
  if (newDate !== null) {
    newDate = transformDateOnChange({
      newDate,
      oldDate,
      mode,
    });
  }
  return newDate;
};

const DefaultBlurView = styled.div`
  display: flex;
  flex-direction: column;
`;

DefaultBlurView.displayName = 'DefaultBlurView';

const CustomInputStyles = styled(InputWeb)<InputStates>`
  appearance: none;
  background-color: white;
  border: ${getStylesForInputStates('border')};
  box-sizing: border-box;
  color: #000000;
  display: flex;
  outline: 0;
  padding-bottom: 0;
  padding-top: 0;
  width: 100%;
  &:focus {
    color: #1a80a2;
    border-color: #1a80a2;
  }
  &::placeholder {
    color: ${getStylesForInputStates('color')({})};
    font-style: ${getStylesForInputStates('fontStyle')({})};
  }
`;

export const CustomInput: React.FC<HTMLProps<HTMLInputElement>> = ({
  style,
  ...rest
}: {
  style?: CSSProperties;
}): ReactElement => {
  const {
    height,
    borderRadius,
    paddingLeft,
    paddingRight,
    fontSize,
  } = useGetDynamicInputStyles();
  return (
    <CustomInputStyles
      {...rest}
      readOnly={true}
      style={{
        ...(style || {}),
        height,
        paddingLeft,
        paddingRight,
        fontSize,
        borderRadius,
      }}
    />
  );
};

type HTMLInputProps = {
  onChange?: ChangeEventHandler;
  value?: string;
};

// Class component because refs
class InputRenderProp extends React.Component<
  HTMLProps<HTMLInputElement & HTMLInputProps> & { highlight?: boolean }
> {
  render(): ReactElement {
    return <CustomInput {...this.props} />;
  }
}

// react-datepicker blurs the input as soon as you click on the calendar view
// this will keep focus on the calendar
export const DefaultBlurBlocker = ({
  children,
  questionInFocus,
}: {
  children: React.ReactNode;
  questionInFocus: boolean;
}): ReactElement => {
  return (
    <DefaultBlurView
      onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
        if (questionInFocus) {
          e.preventDefault();
        }
      }}
    >
      {children}
    </DefaultBlurView>
  );
};

export const TimePicker = ({
  date,
  onDateChange,
  minDate,
  maxDate,
  highlight,
}: RawDatePickersProps): ReactElement => {
  const { onFocus, onBlur, questionInFocus } = useInputFocusContext();

  return (
    <DefaultBlurBlocker questionInFocus={questionInFocus}>
      <ReactDatePicker
        onChange={(changedDate: Date | null): void => {
          const newDate = tryTransformDateOnChange(changedDate, date, 'time');
          onDateChange(getDateWithoutMilliseconds(newDate));
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        selected={date}
        dateFormat={DatePickerDisplayFormat.TIME_FORMAT_12H}
        placeholderText={DatePickerPlaceholder.TIME_PLACEHOLDER_12H}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={15}
        minDate={minDate}
        maxDate={maxDate}
        customInput={
          <InputRenderProp
            data-testid={'TimePicker-input'}
            highlight={highlight}
          />
        }
        popperClassName="react-datepicker-popper-override"
      />
    </DefaultBlurBlocker>
  );
};

export const DatePicker = ({
  date,
  onDateChange,
  minDate,
  maxDate,
  highlight,
}: RawDatePickersProps): ReactElement => {
  const { onFocus, onBlur, questionInFocus } = useInputFocusContext();
  return (
    <DefaultBlurBlocker questionInFocus={questionInFocus}>
      <ReactDatePicker
        {...roles('DatePicker')}
        onChange={(changedDate: Date | null): void => {
          const newDate = tryTransformDateOnChange(changedDate, date, 'date');
          onDateChange(getDateWithoutMilliseconds(newDate));
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        selected={date}
        dateFormat={DatePickerDisplayFormat.DATE_FORMAT}
        placeholderText={DatePickerPlaceholder.DATE_PLACEHOLDER}
        minDate={minDate ? createTimezoneOffsetDate(minDate) : minDate}
        maxDate={maxDate ? createTimezoneOffsetDate(maxDate) : maxDate}
        customInput={
          <InputRenderProp
            data-testid={'DatePicker-input'}
            highlight={highlight}
          />
        }
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <button
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              onClick={decreaseMonth}
            />
            <div className="react-datepicker__current-month">
              {format(date, 'MMMM yyyy')}
            </div>

            <button
              className="react-datepicker__navigation react-datepicker__navigation--next"
              onClick={increaseMonth}
            />
          </div>
        )}
        popperClassName="react-datepicker-popper-override"
      />
    </DefaultBlurBlocker>
  );
};

export const DateTimePicker = ({
  date,
  onDateChange,
  minDate,
  maxDate,
  highlight,
  schedule_delay_in_minutes,
}: RawDatePickersProps): ReactElement => {
  const { onFocus, onBlur, questionInFocus } = useInputFocusContext();
  // TODO: remove `z-index: auto !important` from index.html and replace this
  // code with the `inline` version of the selector and our own portal
  // WHEN: when we get time

  const minTime = getMinTime(minDate, schedule_delay_in_minutes);
  const currentMinTime = getCurrentMinTime(date, minTime);

  return (
    <DefaultBlurBlocker questionInFocus={questionInFocus}>
      <ReactDatePicker
        onChange={(changedDate: Date | null): void => {
          if (isBeforeMinTime(changedDate, minTime)) {
            onDateChange(getDateWithoutMilliseconds(minTime));
          } else {
            const newDate = tryTransformDateOnChange(
              changedDate,
              date,
              'datetime'
            );
            onDateChange(getDateWithoutMilliseconds(newDate));
          }
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        selected={date}
        dateFormat={DatePickerDisplayFormat.DATE_TIME_FORMAT_12H}
        placeholderText={DatePickerPlaceholder.DATE_TIME_PLACEHOLDER_12H}
        showTimeSelect
        timeFormat={DatePickerDisplayFormat.TIME_FORMAT_12H}
        timeIntervals={15}
        openToDate={minDate}
        minTime={currentMinTime}
        maxTime={setHours(setMinutes(new Date(), 45), 23)}
        minDate={minDate}
        maxDate={maxDate}
        popperModifiers={{
          preventOverflow: {
            enabled: true,
          },
        }}
        onCalendarOpen={() => {
          /**
           * TODO don't hide popper on touchstart
           * WHY if popper is out of viewport, mobile can't scroll to it
           * WHEN
           * HOW Start at https://github.com/Hacker0x01/react-datepicker/issues/512
           */
          setTimeout(() => {
            document
              .querySelector('.react-datepicker-popper')
              ?.scrollIntoView();
          });
        }}
        // This API is SO STUPID! Why use JSX like this? So anyway, we need to disable this error...
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        customInput={
          <InputRenderProp
            data-testid={'DateTimePicker-input'}
            highlight={highlight}
          />
        }
        // we're rendering a custom header because the built-in header isn't rendering as expected
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <button
              className="react-datepicker__navigation react-datepicker__navigation--previous"
              onClick={decreaseMonth}
            />
            <div className="react-datepicker__current-month">
              {format(date, 'MMMM yyyy')}
            </div>

            <button
              className="react-datepicker__navigation react-datepicker__navigation--next"
              onClick={increaseMonth}
            />
          </div>
        )}
        popperClassName="react-datepicker-popper-override"
      />
    </DefaultBlurBlocker>
  );
};
